import { API_BASE_URL, API_ENDPOINTS } from '../config';
import streamChat from '../utils/streamChat';

let _onAuthExpired = null;
let _onKickedOut = null;

export const setOnAuthExpired = (callback) => {
  _onAuthExpired = callback;
};

export const setOnKickedOut = (callback) => {
  _onKickedOut = callback;
};

// ========== 获取当前登录用户ID ==========
const getCurrentUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.user_id || 0;
  } catch {
    return 0;
  }
};

// ========== 获取当前登录token ==========
const getCurrentToken = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.token || '';
  } catch {
    return '';
  }
};

// ========== 更新本地存储的token ==========
export const updateLocalToken = (newToken) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.user_id) { user.token = newToken; localStorage.setItem('user', JSON.stringify(user)); }
  } catch {}
};

// ========== 通用请求函数（自动注入user_id + token）==========
export const request = async (endpoint, data = {}, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const authExcludedEndpoints = ['/login.php', '/register.php', '/find_user.php', '/reset_password.php'];
  const isAuthEndpoint = authExcludedEndpoints.some(ep => endpoint.includes(ep));

  if (!isAuthEndpoint) {
    data.user_id = data.user_id || getCurrentUserId();
    data.token = data.token || getCurrentToken();
  }

  const timeout = options.timeout || 15000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data),
      signal: controller.signal
    });

    clearTimeout(timer);

    if (!response.ok && response.status !== 200) {
      let errorText = '';
      try { errorText = await response.text(); } catch {}
      if (response.status === 502) {
        return { success: false, data: null, msg: '后端服务未启动，请检查PHP服务是否运行' };
      }
      if (response.status === 504 || response.status === 503) {
        return { success: false, data: null, msg: '服务器繁忙，请稍后重试' };
      }
      try {
        const errResult = JSON.parse(errorText);
        return { success: false, code: errResult.code || response.status, data: null, msg: errResult.msg || '请求失败' };
      } catch {
        return { success: false, data: null, msg: `服务器错误(${response.status})` };
      }
    }

    const result = await response.json();

    if (result.code === 200 || result.code === 202) {
      return { success: true, code: result.code, data: result.data, msg: result.msg };
    } else {
      const noRedirectEndpoints = ['/login.php', '/register.php', '/find_user.php', '/reset_password.php'];
      const isNoRedirectEndpoint = noRedirectEndpoints.some(ep => endpoint.includes(ep));
      if (!isNoRedirectEndpoint && result.code === 4011) {
        localStorage.removeItem('user');
        if (_onKickedOut) {
          _onKickedOut(result.msg);
        }
      } else if (!isNoRedirectEndpoint && (result.code === 403 || result.code === 401)) {
        localStorage.removeItem('user');
        if (_onAuthExpired) {
          _onAuthExpired(result.code, result.msg);
        } else {
          window.location.hash = '#/';
        }
      }
      return { success: false, code: result.code || 400, data: null, msg: result.msg || '请求失败' };
    }
  } catch (error) {
    clearTimeout(timer);
    if (error.name === 'AbortError') {
      return { success: false, data: null, msg: '请求超时，服务器未响应。请检查：1)PHP服务是否运行 2)数据库是否启动 3)网络是否正常' };
    }
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      return { success: false, data: null, msg: '无法连接服务器，请检查PHP服务是否运行' };
    }
    console.error('API Error:', error);
    return { success: false, data: null, msg: '网络错误，请稍后重试' };
  }
};

// ========== 用户相关接口 ==========
export const api = {
  // 登录
  login: async (account, password) => {
    return await request(API_ENDPOINTS.login, { account, password });
  },
  loginByPhone: async (phone, code, inviteCode) => {
    return await request('/login.php', { action: 'phone_login', phone, verify_code: code, invite_code: inviteCode || '' });
  },

  findUser: async (account) => {
    return await request('/find_user.php', { account });
  },

  // 注册 - 用户名
  registerUsername: async (username, password, inviteCode) => {
    return await request(API_ENDPOINTS.register, {
      register_type: 'username',
      username,
      password,
      invite_code: inviteCode
    });
  },

  // 注册 - 手机号
  registerPhone: async (phone, verifyCode, password, inviteCode) => {
    return await request(API_ENDPOINTS.register, {
      register_type: 'phone',
      phone,
      verify_code: verifyCode,
      password,
      invite_code: inviteCode
    });
  },

  // 注册 - 邮箱
  registerEmail: async (email, password, confirmPassword, inviteCode) => {
    return await request(API_ENDPOINTS.register, {
      register_type: 'email',
      email,
      password,
      confirm_password: confirmPassword,
      invite_code: inviteCode
    });
  },

  // 获取列表
  getList: async (params = {}) => {
    return await request(API_ENDPOINTS.get_list, params);
  },

  // 上传文件
  upload: async (formData) => {
    const url = `${API_BASE_URL}${API_ENDPOINTS.upload}`;
    formData.append('user_id', getCurrentUserId());
    formData.append('token', getCurrentToken());
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (result.code === 200) {
        return { success: true, code: 200, data: result.data, msg: result.msg };
      } else {
        return { success: false, code: result.code || 400, data: null, msg: result.msg || '上传失败' };
      }
    } catch (error) {
      console.error('Upload Error:', error);
      return { success: false, data: null, msg: '上传失败，请稍后重试' };
    }
  },

  // 保存内容
  save: async (data) => {
    return await request(API_ENDPOINTS.save, data);
  },

  // ========== Roadmap 页面接口 ==========
  roadmap: {
    generate: async (answers) => {
      return await request(API_ENDPOINTS.roadmap, {
        action: 'generate',
        answers
      }, { timeout: 180000 });
    },
    getStatus: async (recordId) => {
      return await request(API_ENDPOINTS.roadmap, {
        action: 'get_status',
        record_id: recordId
      });
    },
    getReport: async (recordId) => {
      return await request(API_ENDPOINTS.roadmap, {
        action: 'get_report',
        record_id: recordId
      });
    },
    save: async (data) => {
      return await request(API_ENDPOINTS.roadmap, {
        action: 'save',
        ...data
      });
    }
  },

  // ========== Content 页面接口 ==========
  content: {
    generateCopy: async (prompt) => {
      return await request(API_ENDPOINTS.content, {
        action: 'generate_copy',
        prompt
      });
    },
    generateScript: async (prompt) => {
      return await request(API_ENDPOINTS.content, {
        action: 'generate_script',
        prompt
      });
    },
    generateStoryboard: async (config) => {
      return await request(API_ENDPOINTS.content, {
        action: 'generate_storyboard',
        ...config
      });
    },
    save: async (data) => {
      return await request(API_ENDPOINTS.content, {
        action: 'save',
        ...data
      });
    }
  },

  // ========== Video 页面接口 ==========
  video: {
    generate: async (config) => {
      return await request(API_ENDPOINTS.video, {
        action: 'generate',
        ...config
      });
    },
    getTasks: async () => {
      return await request(API_ENDPOINTS.video, {
        action: 'get_tasks'
      });
    },
    deleteTask: async (taskId) => {
      return await request(API_ENDPOINTS.video, {
        action: 'delete_task',
        task_id: taskId
      });
    }
  },

  // ========== Analytics 页面接口 ==========
  analytics: {
    analyze: async (douyinId) => {
      return await request(API_ENDPOINTS.analytics, {
        action: 'analyze',
        douyin_id: douyinId
      });
    },
    getHistory: async () => {
      return await request(API_ENDPOINTS.analytics, {
        action: 'get_history'
      });
    }
  },

  // ========== Rewrite 页面接口 ==========
  rewrite: {
    extract: async (content) => {
      return await request(API_ENDPOINTS.rewrite, {
        action: 'extract',
        content
      });
    },
    rewrite: async (content, style) => {
      return await request(API_ENDPOINTS.rewrite, {
        action: 'rewrite',
        content,
        style
      });
    },
    save: async (data) => {
      return await request(API_ENDPOINTS.rewrite, {
        action: 'save',
        ...data
      });
    }
  },

  // ========== Account 页面接口 ==========
  account: {
    getAccounts: async () => {
      return await request(API_ENDPOINTS.account, {
        action: 'get_accounts'
      });
    },
    addAccount: async (account) => {
      return await request(API_ENDPOINTS.account, {
        action: 'add_account',
        ...account
      });
    },
    deleteAccount: async (accountId) => {
      return await request(API_ENDPOINTS.account, {
        action: 'delete_account',
        account_id: accountId
      });
    }
  },

  // ========== Mixer 页面接口 ==========
  mixer: {
    getClips: async () => {
      return await request(API_ENDPOINTS.mixer, {
        action: 'get_clips'
      });
    },
    saveProject: async (project) => {
      return await request(API_ENDPOINTS.mixer, {
        action: 'save_project',
        ...project
      });
    },
    export: async (projectId) => {
      return await request(API_ENDPOINTS.mixer, {
        action: 'export',
        project_id: projectId
      });
    }
  },

  // ========== Drama 页面接口 ==========
  drama: {
    generateVideo: async (config) => {
      return await request(API_ENDPOINTS.drama, {
        action: 'generate_video',
        ...config
      });
    },
    getTemplates: async () => {
      return await request(API_ENDPOINTS.drama, {
        action: 'get_templates'
      });
    },
    saveProject: async (project) => {
      return await request(API_ENDPOINTS.drama, {
        action: 'save_project',
        ...project
      });
    }
  },

  // ========== Music 页面接口 ==========
  music: {
    generate: async (config) => {
      return await request(API_ENDPOINTS.music, {
        action: 'generate',
        ...config
      });
    },
    getMyMusic: async () => {
      return await request(API_ENDPOINTS.music, {
        action: 'get_my_music'
      });
    },
    deleteMusic: async (musicId) => {
      return await request(API_ENDPOINTS.music, {
        action: 'delete_music',
        music_id: musicId
      });
    },
    download: async (musicId, format) => {
      return await request(API_ENDPOINTS.music, {
        action: 'download',
        music_id: musicId,
        format
      });
    }
  },

  // ========== Workflow 工作流接口 ==========
  workflow: {
    save: async (projectData) => {
      return await request(API_ENDPOINTS.workflow, {
        action: 'save',
        ...projectData
      });
    },
    list: async () => {
      return await request(API_ENDPOINTS.workflow, {
        action: 'list'
      });
    },
    load: async (projectId) => {
      return await request(API_ENDPOINTS.workflow, {
        action: 'load',
        project_id: projectId
      });
    },
    deleteProject: async (projectId) => {
      return await request(API_ENDPOINTS.workflow, {
        action: 'delete',
        project_id: projectId
      });
    },
    hide: async (projectId) => {
      return await request(API_ENDPOINTS.workflow, {
        action: 'hide',
        project_id: projectId
      });
    },
    getOrCreate: async () => {
      return await request(API_ENDPOINTS.workflow, {
        action: 'get_or_create'
      });
    },
    rename: async (projectId, newName) => {
      return await request(API_ENDPOINTS.workflow, {
        action: 'rename',
        project_id: projectId,
        name: newName
      });
    }
  },

  // ========== Profile 用户信息接口 ==========
  profile: {
    getUserInfo: async (userId) => {
      return await request('/profile.php', { action: 'get', user_id: userId });
    },
    updateUsername: async (userId, username) => {
      return await request('/profile.php', { action: 'update_username', user_id: userId, username });
    },
    updatePassword: async (userId, currentPassword, newPassword) => {
      return await request('/profile.php', { action: 'update_password', user_id: userId, current_password: currentPassword, new_password: newPassword });
    },
    setPassword: async (userId, newPassword, confirmPassword) => {
      return await request('/profile.php', { action: 'set_password', user_id: userId, new_password: newPassword, confirm_password: confirmPassword });
    },

    resetPassword: async (userId, newPassword) => {
      return await request('/reset_password.php', { action: 'reset', user_id: userId, new_password: newPassword });
    },

    resetPasswordByVerify: async (data) => {
      return await request('/reset_password.php', { action: 'reset_by_verify', ...data });
    },

    deactivateAccount: async (userId, password) => {
      return await request('/deactivate_account.php', { action: 'deactivate', user_id: userId, password });
    },
    bindPhone: async (userId, phone, verifyCode) => {
      return await request('/profile.php', { action: 'bind_phone', user_id: userId, phone, verify_code: verifyCode });
    },
    bindEmail: async (userId, email, verifyCode) => {
      return await request('/profile.php', { action: 'bind_email', user_id: userId, email, verify_code: verifyCode });
    },
    uploadAvatar: async (userId, file) => {
      const formData = new FormData();
      formData.append('avatar', file);
      formData.append('action', 'upload');
      formData.append('user_id', userId);
      formData.append('token', getCurrentToken());
      
      const response = await fetch(API_BASE_URL + '/avatar.php', {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      if (result.code === 200) {
        return { success: true, code: 200, data: result.data, msg: result.msg };
      } else {
        return { success: false, code: result.code || 400, data: null, msg: result.msg || '上传失败' };
      }
    }
  },

  // ========== Notification 通知接口 ==========
  notification: {
    getAll: async () => {
      return await request('/notification.php', { action: 'get_all' });
    },
    markRead: async (notifId, source = 'user') => {
      return await request('/notification.php', { action: 'mark_read', notif_id: notifId, source });
    },
    markAllRead: async () => {
      return await request('/notification.php', { action: 'mark_all_read' });
    },
    getDetail: async (notifId, source) => {
      return await request('/notification.php', { action: 'get_detail', notif_id: notifId, source });
    },
    getUnreadCount: async () => {
      return await request('/notification.php', { action: 'get_unread_count' });
    },
    add: async (data) => {
      return await request('/notification.php', { action: 'add_user_notif', ...data });
    }
  },

  // ========== MySpace 个人空间接口 ==========
  myspace: {
    getAllData: async () => {
      return await request('/myspace_data.php', { action: 'get_all' });
    }
  },

  // ========== Subscribe 订阅服务接口 ==========
  subscribe: {
    getAccount: async () => {
      return await request('/subscribe.php', { action: 'get_account' });
    },
    getOrders: async () => {
      return await request('/subscribe.php', { action: 'get_orders' });
    },
    getUsage: async () => {
      return await request('/subscribe.php', { action: 'get_usage' });
    },
    createOrder: async (data) => {
      return await request('/subscribe.php', { action: 'create_order', ...data });
    }
  },

  // ========== CloudSpace 云空间接口 ==========
  cloudSpace: {
    getAll: async () => {
      return await request('/cloud_space.php', { action: 'get_all' });
    },
    upload: async (userId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('action', 'upload');
      formData.append('user_id', userId);
      formData.append('token', getCurrentToken());
      const response = await fetch(API_BASE_URL + '/cloud_space.php', {
        method: 'POST', body: formData
      });
      const result = await response.json();
      if (result.code === 200) {
        return { success: true, code: 200, data: result.data, msg: result.msg };
      } else {
        return { success: false, code: result.code || 400, data: null, msg: result.msg || '上传失败' };
      }
    },
    delete: async (fileId, source, category) => {
      return await request('/cloud_space.php', { action: 'delete', file_id: fileId, source, category });
    }
  },

  // ========== AI 流式输出接口（火山引擎/Ark） ==========
  ai: {
    /**
     * 流式生成文案
     * @param {string} prompt - 用户输入
     * @param {Object} options - 配置选项
     * @param {Function} options.onChunk - 每个文本块回调
     * @param {Function} options.onComplete - 完成回调
     * @param {Function} options.onError - 错误回调
     * @returns {AbortController}
     */
    streamGenerateCopy: (prompt, options = {}) => {
      const systemPrompt = `你是一个专业的短视频文案创作助手。根据用户输入的主题，生成吸引人的短视频文案，要求：
1. 标题要吸引眼球，使用emoji和数字
2. 正文要有情感共鸣，口语化表达
3. 结尾要有引导互动的CTA
4. 适合抖音、小红书等平台
5. 长度控制在200-300字`;

      return streamChat.streamChat({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ],
        ...options
      });
    },

    /**
     * 流式生成脚本
     */
    streamGenerateScript: (prompt, options = {}) => {
      const systemPrompt = `你是一个专业的短视频脚本创作专家。根据用户输入的主题，生成详细的拍摄脚本，要求：
1. 包含分镜表格（镜头号、景别、运镜、画面内容、台词、音效、时长）
2. 每个镜头都要有具体的画面描述和台词
3. 时长控制在30-60秒
4. 符合短视频平台的节奏特点
5. 包含拍摄技巧提示`;

      return streamChat.streamChat({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ],
        ...options
      });
    },

    /**
     * 流式生成分镜
     */
    streamGenerateStoryboard: (prompt, options = {}) => {
      const systemPrompt = `你是一个专业的分镜设计师。根据用户的视频主题或脚本，生成专业的分镜表，要求：
1. 使用表格格式展示（镜头号|景别|运镜|画面描述|时长|备注）
2. 每个镜头都有详细的视觉描述
3. 包含光影、色彩、构图建议
4. 考虑转场效果
5. 适合手机拍摄执行`;

      return streamChat.streamChat({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ],
        ...options
      });
    },

    /**
     * 流式改写内容
     */
    streamRewrite: (content, style, options = {}) => {
      const systemPrompt = `你是一个专业的内容改写专家。根据目标风格改写用户提供的内容：
- 保持原意不变
- 适配目标平台风格
- 提升可读性和吸引力
- 避免敏感词和违规内容`;

      return streamChat.streamChat({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: `请将以下内容改写为${style}风格：\n\n${content}` }
        ],
        ...options
      });
    },

    /**
     * 企业版流式生成品牌文案
     */
    streamEnterpriseCopy: (prompt, brandInfo, options = {}) => {
      const systemPrompt = `你是一个企业级品牌文案创作专家。根据品牌信息和需求，生成符合广告法规范的商业文案：
1. 严格遵循品牌VI规范和调性
2. 通过广告法合规检查
3. 突出产品卖点和品牌价值
4. 专业、权威但不失亲和力
5. 包含明确的行动号召(CTA)`;

      return streamChat.streamChat({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: `品牌信息：${JSON.stringify(brandInfo)}\n\n需求：${prompt}` }
        ],
        ...options
      });
    },

    /**
     * 通用流式聊天（可用于任何场景）
     */
    streamChat: (messages, options = {}) => {
      return streamChat.streamChat({ messages, ...options });
    },

    /**
     * 快速流式生成（返回Promise）
     */
    quickStream: (prompt, options = {}) => {
      return streamChat.quickStream(prompt, options);
    }
  }
};

export default api;
